import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { API_URL, getAuthHeader } from '@/lib/utils';
import { Save, Loader2, BarChart3, Mail } from 'lucide-react';
import ImageUpload from '@/components/ImageUpload';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '',
    tagline: '',
    phone: '',
    email: '',
    admin_email: '',
    address: '',
    whatsapp: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    hero_title: '',
    hero_subtitle: '',
    about_text: '',
    vision: '',
    mission: '',
    google_analytics_id: '',
    resend_api_key: '',
    stat_facilities: '200+',
    stat_experience_years: '8+',
    stat_success_rate: '100%',
    stat_consultants: '15+',
    stat_facilities_label: 'Healthcare Facilities',
    stat_experience_label: 'Years Experience',
    stat_success_label: 'Success Rate',
    stat_consultants_label: 'Expert Consultants',
    partnership_image_url: '',
    emr_plans_api_url: '',
    page_about_visible: true,
    page_services_visible: true,
    page_products_visible: true,
    page_courses_visible: true,
    page_gallery_visible: true,
    page_blog_visible: true,
    page_contact_visible: true,
    page_hospital_visible: true,
    page_training_visible: true
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings`);
        setSettings(response.data);
      } catch (error) {
        toast.error('Failed to fetch settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings`, settings, {
        headers: getAuthHeader()
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-6">
        <h1 className="font-manrope text-2xl font-bold text-[#1a2a5e]">Site Settings</h1>
        <p className="text-gray-500 mt-1">Manage your website settings and SEO</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6">
          {/* Page Visibility */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-[#1a2a5e]">Page Visibility</CardTitle>
              <p className="text-sm text-gray-500 mt-1">Show or hide pages from the website navigation</p>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { key: 'page_about_visible', label: 'About' },
                  { key: 'page_services_visible', label: 'Services' },
                  { key: 'page_products_visible', label: 'Digital Products/EMR' },
                  { key: 'page_courses_visible', label: 'Courses' },
                  { key: 'page_gallery_visible', label: 'Gallery' },
                  { key: 'page_blog_visible', label: 'Blog' },
                  { key: 'page_contact_visible', label: 'Contact' },
                  { key: 'page_hospital_visible', label: 'For Hospital/Institute' },
                  { key: 'page_training_visible', label: 'Our Training Program' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3" data-testid={`toggle-${key}`}>
                    <span className="text-sm font-medium text-gray-700">{label}</span>
                    <Switch
                      checked={settings[key] !== false}
                      onCheckedChange={(v) => handleChange(key, v)}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Company Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-[#1a2a5e]">Company Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={settings.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tagline</Label>
                  <Input
                    value={settings.tagline}
                    onChange={(e) => handleChange('tagline', e.target.value)}
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={settings.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Public Email</Label>
                  <Input
                    type="email"
                    value={settings.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="info@stewardindia.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Admin Email (for notifications)</Label>
                  <Input
                    type="email"
                    value={settings.admin_email}
                    onChange={(e) => handleChange('admin_email', e.target.value)}
                    placeholder="info@stewardindia.com"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>WhatsApp Number</Label>
                  <Input
                    value={settings.whatsapp}
                    onChange={(e) => handleChange('whatsapp', e.target.value)}
                    placeholder="919616298162"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  value={settings.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Hero Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-[#1a2a5e]">Homepage Hero Section</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Hero Title</Label>
                <Input
                  value={settings.hero_title}
                  onChange={(e) => handleChange('hero_title', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Hero Subtitle</Label>
                <Input
                  value={settings.hero_subtitle}
                  onChange={(e) => handleChange('hero_subtitle', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Courses Page - Partnership Image */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-[#1a2a5e]">EMR/HIS Plans API</CardTitle>
              <p className="text-sm text-gray-500 mt-1">Enter your external API URL that contains all EMR/HIS plans</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Plans API URL</Label>
                <Input
                  value={settings.emr_plans_api_url}
                  onChange={(e) => handleChange('emr_plans_api_url', e.target.value)}
                  placeholder="https://api.stewardindia.com/v1/plans"
                  data-testid="emr-api-url-input"
                />
                <p className="text-xs text-gray-400">The EMR/HIS page will fetch plans from this API</p>
              </div>
            </CardContent>
          </Card>

          {/* Courses Page - Partnership Image */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-[#1a2a5e]">Courses Page - Partnership Image</CardTitle>
              <p className="text-sm text-gray-500 mt-1">Upload the MOU signing ceremony / university partnership image shown on the Courses page</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <ImageUpload
                value={settings.partnership_image_url}
                onChange={(url) => handleChange('partnership_image_url', url)}
                label="University Partnership / MOU Signing Ceremony Photo"
              />
            </CardContent>
          </Card>

          {/* About Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-[#1a2a5e]">About Us Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>About Text</Label>
                <Textarea
                  value={settings.about_text}
                  onChange={(e) => handleChange('about_text', e.target.value)}
                  rows={4}
                  placeholder="Company overview text..."
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vision</Label>
                  <Textarea
                    value={settings.vision}
                    onChange={(e) => handleChange('vision', e.target.value)}
                    rows={3}
                    placeholder="Company vision..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mission</Label>
                  <Textarea
                    value={settings.mission}
                    onChange={(e) => handleChange('mission', e.target.value)}
                    rows={3}
                    placeholder="Company mission..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Homepage Statistics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-[#1a2a5e]">Homepage Statistics</CardTitle>
              <p className="text-sm text-gray-500 mt-1">Edit the stats displayed on the homepage hero section</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Stat 1 - Value</Label>
                  <Input
                    value={settings.stat_facilities}
                    onChange={(e) => handleChange('stat_facilities', e.target.value)}
                    placeholder="200+"
                    data-testid="stat-facilities-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stat 1 - Label</Label>
                  <Input
                    value={settings.stat_facilities_label}
                    onChange={(e) => handleChange('stat_facilities_label', e.target.value)}
                    placeholder="Healthcare Facilities"
                    data-testid="stat-facilities-label-input"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Stat 2 - Value</Label>
                  <Input
                    value={settings.stat_experience_years}
                    onChange={(e) => handleChange('stat_experience_years', e.target.value)}
                    placeholder="8+"
                    data-testid="stat-experience-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stat 2 - Label</Label>
                  <Input
                    value={settings.stat_experience_label}
                    onChange={(e) => handleChange('stat_experience_label', e.target.value)}
                    placeholder="Years Experience"
                    data-testid="stat-experience-label-input"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Stat 3 - Value</Label>
                  <Input
                    value={settings.stat_success_rate}
                    onChange={(e) => handleChange('stat_success_rate', e.target.value)}
                    placeholder="100%"
                    data-testid="stat-success-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stat 3 - Label</Label>
                  <Input
                    value={settings.stat_success_label}
                    onChange={(e) => handleChange('stat_success_label', e.target.value)}
                    placeholder="Success Rate"
                    data-testid="stat-success-label-input"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Stat 4 - Value</Label>
                  <Input
                    value={settings.stat_consultants}
                    onChange={(e) => handleChange('stat_consultants', e.target.value)}
                    placeholder="15+"
                    data-testid="stat-consultants-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stat 4 - Label</Label>
                  <Input
                    value={settings.stat_consultants_label}
                    onChange={(e) => handleChange('stat_consultants_label', e.target.value)}
                    placeholder="Expert Consultants"
                    data-testid="stat-consultants-label-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SEO Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-[#1a2a5e]">SEO Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Meta Title</Label>
                <Input
                  value={settings.meta_title}
                  onChange={(e) => handleChange('meta_title', e.target.value)}
                  placeholder="Page title for search engines"
                />
                <p className="text-xs text-gray-500">{settings.meta_title?.length || 0}/60 characters recommended</p>
              </div>
              <div className="space-y-2">
                <Label>Meta Description</Label>
                <Textarea
                  value={settings.meta_description}
                  onChange={(e) => handleChange('meta_description', e.target.value)}
                  rows={3}
                  placeholder="Description for search engines"
                />
                <p className="text-xs text-gray-500">{settings.meta_description?.length || 0}/160 characters recommended</p>
              </div>
              <div className="space-y-2">
                <Label>Meta Keywords</Label>
                <Textarea
                  value={settings.meta_keywords}
                  onChange={(e) => handleChange('meta_keywords', e.target.value)}
                  rows={2}
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>
            </CardContent>
          </Card>

          {/* Integrations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-[#1a2a5e]">Integrations</CardTitle>
              <p className="text-sm text-gray-500 mt-1">Configure third-party services. You can add these anytime.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Google Analytics */}
              <div className="p-4 rounded-lg border border-gray-200 bg-gray-50/50 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-[#1a2a5e]" />
                    <Label className="text-base font-semibold">Google Analytics</Label>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${settings.google_analytics_id ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {settings.google_analytics_id ? 'Active' : 'Not Configured'}
                  </span>
                </div>
                <Input
                  value={settings.google_analytics_id}
                  onChange={(e) => handleChange('google_analytics_id', e.target.value)}
                  placeholder="G-XXXXXXXXXX"
                  data-testid="ga-tracking-input"
                />
                <div className="text-xs text-gray-500 space-y-1">
                  <p className="font-medium text-gray-600">How to get your Tracking ID:</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-gray-500">
                    <li>Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-[#1a2a5e] underline">analytics.google.com</a></li>
                    <li>Click Admin (gear icon) &rarr; Property &rarr; Data Streams</li>
                    <li>Select your web stream (or create one for stewardindia.com)</li>
                    <li>Copy the <strong>Measurement ID</strong> (starts with G-)</li>
                    <li>Paste it above and click <strong>Save Settings</strong></li>
                  </ol>
                </div>
              </div>

              {/* Resend Email */}
              <div className="p-4 rounded-lg border border-gray-200 bg-gray-50/50 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-[#1a2a5e]" />
                    <Label className="text-base font-semibold">Email Notifications (Resend)</Label>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${settings.resend_api_key ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {settings.resend_api_key ? 'Active' : 'Not Configured'}
                  </span>
                </div>
                <Input
                  type="password"
                  value={settings.resend_api_key}
                  onChange={(e) => handleChange('resend_api_key', e.target.value)}
                  placeholder="re_xxxxxxxx"
                  data-testid="resend-key-input"
                />
                <div className="text-xs text-gray-500 space-y-1">
                  <p className="font-medium text-gray-600">How to get your Resend API Key:</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-gray-500">
                    <li>Sign up at <a href="https://resend.com/signup" target="_blank" rel="noopener noreferrer" className="text-[#1a2a5e] underline">resend.com/signup</a> (free plan available)</li>
                    <li>Go to <strong>API Keys</strong> in the dashboard</li>
                    <li>Click <strong>Create API Key</strong> and copy it</li>
                    <li>Paste it above and click <strong>Save Settings</strong></li>
                    <li><strong>Important:</strong> Also verify your domain (stewardindia.com) under <strong>Domains</strong> in Resend</li>
                  </ol>
                  <p className="mt-2 text-gray-600">Once configured, you'll receive an email at <strong>{settings.admin_email || 'info@stewardindia.com'}</strong> whenever a customer submits an inquiry.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button type="submit" className="bg-[#1a2a5e] hover:bg-[#2d4a8e]" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
